import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSetupTwoFactorMutation,
  useActivateTwoFactorMutation,
  useDisableTwoFactorMutation,
  useWhoAmIQuery,
} from '../../../generated/graphql';
import { toast } from 'react-toastify';
import { Button } from '../../../releox-engine/react/buttons/Button';
import { Card } from '../../../components/Card';
import { CardTitle } from '../../../components/CardTitle';

export const TwoFactorScene = () => {
  const { t } = useTranslation('TwoFactorScene');
  const [setupData, setSetupData] = useState<{ secret: string; qrCodeUrl: string } | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [password, setPassword] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);

  const { data: userData, refetch } = useWhoAmIQuery();
  const [setupTwoFactor, { loading: setupLoading }] = useSetupTwoFactorMutation({
    onCompleted: (data) => {
      setSetupData({
        secret: data.setupTwoFactor.secret,
        qrCodeUrl: data.setupTwoFactor.qrCodeUrl,
      });
    },
  });

  const [activateTwoFactor, { loading: activateLoading }] = useActivateTwoFactorMutation({
    onCompleted: () => {
      toast.success(String(t('2faActivated')));
      setSetupData(null);
      setVerificationToken('');
      refetch();
    },
  });

  const [disableTwoFactor, { loading: disableLoading }] = useDisableTwoFactorMutation({
    onCompleted: () => {
      toast.success(String(t('2faDisabled')));
      setShowDisableModal(false);
      setPassword('');
      refetch();
    },
  });

  const handleSetup = () => {
    setupTwoFactor();
  };

  const handleActivate = (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationToken.length === 6) {
      activateTwoFactor({
        variables: { token: verificationToken },
      });
    } else {
      toast.error(String(t('invalidTokenLength')));
    }
  };

  const handleDisable = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      disableTwoFactor({
        variables: { password },
      });
    } else {
      toast.error(String(t('passwordRequired')));
    }
  };

  const isTwoFactorEnabled = userData?.whoAmI?.twoFactorEnabled;

  return (
    <div className="mx-auto w-full max-w-3xl sm:px-2">
      {!setupData && !isTwoFactorEnabled && (
        <Card>
          <CardTitle>{t('enable2fa')}</CardTitle>
          <p className="text-gray-600 mb-4">{t('2faDescription')}</p>
          <Button
            type="button"
            onClick={handleSetup}
            disabled={setupLoading}
            loading={setupLoading}
          >
            {t('enable2faButton')}
          </Button>
        </Card>
      )}

      {setupData && (
        <Card>
          <CardTitle>{t('scanQrCode')}</CardTitle>

          <div className="mb-6 flex justify-center">
            <img src={setupData.qrCodeUrl} alt="2FA QR Code" className="w-64 h-64" />
          </div>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">{t('orEnterManually')}</p>
            <div className="bg-gray-100 p-3 rounded">
              <code className="text-sm break-all">{setupData.secret}</code>
            </div>
          </div>

          <form onSubmit={handleActivate}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('verificationCode')}
              </label>
              <input
                type="text"
                maxLength={6}
                value={verificationToken}
                onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <Button
              type="submit"
              disabled={verificationToken.length !== 6}
              loading={activateLoading}
            >
              {t('activate')}
            </Button>
          </form>
        </Card>
      )}

      {isTwoFactorEnabled && !setupData && (
        <Card>
          <CardTitle>
            <div className="flex items-center">
              <svg
                className="w-4 h-4 text-green-600 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{t('2faIsActive')}</span>
            </div>
          </CardTitle>

          <p className="text-gray-600 mb-4">{t('2faActiveDescription')}</p>

          <Button
            type="button"
            onClick={() => setShowDisableModal(true)}
          >
            {t('disable2fa')}
          </Button>
        </Card>
      )}

      {showDisableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">{t('confirmDisable2fa')}</h3>
            <p className="text-gray-600 mb-4">{t('enterPasswordToDisable')}</p>

            <form onSubmit={handleDisable}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('password')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={() => {
                    setShowDisableModal(false);
                    setPassword('');
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={disableLoading}
                >
                  {t('disable')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
